import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  Spinner,
} from "@/components/primitives";
import { Icon } from "@/components/Icon";
import { api } from "@/lib/api";
import type {
  AdapterKind,
  CatalogCategory,
  CatalogEntry,
  StatuspagePreview,
} from "@/lib/types";

/** How each adapter is described to an operator choosing a provider. */
const ADAPTER_LABEL: Record<AdapterKind, string> = {
  statuspage: "Statuspage JSON",
  rss: "RSS",
  atom: "Atom",
  none: "Not available",
};

type FeedPreview = Awaited<ReturnType<typeof api.parseRss>>;
type Preview =
  | { kind: "statuspage"; data: StatuspagePreview }
  | { kind: "feed"; data: FeedPreview };

const selectStyle = {
  height: 36,
  padding: "0 10px",
  borderRadius: 8,
  border: "1px solid var(--input)",
  background: "var(--background)",
  color: "var(--foreground)",
  fontSize: 13,
} as const;

export function ProviderCatalog() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [adapter, setAdapter] = useState("");
  const [entries, setEntries] = useState<CatalogEntry[] | null>(null);
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CatalogEntry | null>(null);
  // Source URLs already monitored, so a provider cannot be added twice. Matched
  // on the ingestion URL rather than the name: that is what the store treats as
  // the identity of a feed.
  const [configured, setConfigured] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setEntries(null);
    api
      .listCatalog({
        q: query,
        category: category || undefined,
        adapter: (adapter || undefined) as AdapterKind | undefined,
      })
      .then((result) => {
        if (cancelled) return;
        setEntries(result.entries);
        setCategories(result.categories);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [query, category, adapter]);

  useEffect(() => {
    api
      .listServices()
      .then((services) => setConfigured(new Set(services.map((s) => s.sourceUrl))))
      .catch(() => setConfigured(new Set()));
  }, []);

  if (selected) {
    return (
      <ProviderDetail
        entry={selected}
        alreadyConfigured={configured.has(selected.ingestUrl)}
        onBack={() => setSelected(null)}
      />
    );
  }

  return (
    <Card>
      <CardHeader
        title="Provider catalog"
        description="Verified status sources. Selecting one uses the endpoint we checked — no URL to find or paste."
      />
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr", gap: 12 }}>
          <Field label="Search">
            <Input
              value={query}
              onChange={setQuery}
              placeholder="Provider name…"
            />
          </Field>
          <Field label="Category">
            <select
              aria-label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={selectStyle}
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label} ({c.count})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Delivery method">
            <select
              aria-label="Delivery method"
              value={adapter}
              onChange={(e) => setAdapter(e.target.value)}
              style={selectStyle}
            >
              <option value="">All methods</option>
              <option value="statuspage">Statuspage JSON</option>
              <option value="rss">RSS</option>
              <option value="atom">Atom</option>
            </select>
          </Field>
        </div>

        {error && <ErrorNote message={error} />}

        {!entries && !error && (
          <div
            role="status"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              color: "var(--muted-foreground)",
              padding: 20,
            }}
          >
            <Spinner />
            Loading providers…
          </div>
        )}

        {entries && entries.length === 0 && (
          <p
            style={{
              fontSize: 13,
              color: "var(--muted-foreground)",
              padding: "20px 4px",
              margin: 0,
            }}
          >
            No providers match those filters. Use the RSS, Statuspage, or Webhook
            tabs to add one that isn't in the catalog.
          </p>
        )}

        {entries && entries.length > 0 && (
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              borderRadius: 10,
              boxShadow: "0 0 0 1px var(--border)",
              overflow: "hidden",
            }}
          >
            {entries.map((entry, i) => (
              <li key={entry.id}>
                <ProviderRow
                  entry={entry}
                  first={i === 0}
                  alreadyConfigured={configured.has(entry.ingestUrl)}
                  onSelect={() => setSelected(entry)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

function ProviderRow({
  entry,
  first,
  alreadyConfigured,
  onSelect,
}: {
  entry: CatalogEntry;
  first: boolean;
  alreadyConfigured: boolean;
  onSelect: () => void;
}) {
  const unavailable = !entry.enabled;
  const blocked = unavailable || alreadyConfigured;
  const hint = unavailable
    ? entry.disabledReason
    : alreadyConfigured
      ? "Already being monitored"
      : `Verified ${entry.lastVerifiedAt}`;

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={blocked}
      aria-disabled={blocked}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        border: "none",
        borderTop: first ? "none" : "1px solid var(--border)",
        background: "transparent",
        textAlign: "left",
        cursor: blocked ? "not-allowed" : "pointer",
        opacity: blocked ? 0.55 : 1,
        color: "var(--foreground)",
        font: "inherit",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{entry.name}</div>
        <div
          style={{
            fontSize: 11,
            color: "var(--muted-foreground)",
            marginTop: 2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {hint}
        </div>
      </div>
      <AdapterBadge adapter={entry.adapter} />
      {!blocked && <Icon name="chevron" size={14} />}
    </button>
  );
}

function AdapterBadge({ adapter }: { adapter: AdapterKind }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 500,
        padding: "3px 8px",
        borderRadius: 999,
        background: "var(--muted)",
        color: "var(--muted-foreground)",
        whiteSpace: "nowrap",
      }}
    >
      {ADAPTER_LABEL[adapter]}
    </span>
  );
}

function ProviderDetail({
  entry,
  alreadyConfigured,
  onBack,
}: {
  entry: CatalogEntry;
  alreadyConfigured: boolean;
  onBack: () => void;
}) {
  const navigate = useNavigate();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const request: Promise<Preview> =
      entry.adapter === "statuspage"
        ? api
            .previewStatuspage(entry.ingestUrl)
            .then((data) => ({ kind: "statuspage", data }) as Preview)
        : api
            .parseRss(entry.ingestUrl)
            .then((data) => ({ kind: "feed", data }) as Preview);
    request
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError((e as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entry]);

  const onAdd = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.createServiceFromCatalog({ catalogId: entry.id });
      navigate("/admin/services");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader title={entry.name} description={entry.notes || undefined} />
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "8px 16px",
            margin: 0,
            fontSize: 12,
          }}
        >
          <dt style={{ color: "var(--muted-foreground)" }}>Status page</dt>
          <dd style={{ margin: 0 }}>
            <a
              href={entry.statusPageUrl}
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: "var(--primary)" }}
            >
              {entry.statusPageUrl}
            </a>
          </dd>
          <dt style={{ color: "var(--muted-foreground)" }}>Delivery method</dt>
          <dd style={{ margin: 0 }}>{ADAPTER_LABEL[entry.adapter]}</dd>
          <dt style={{ color: "var(--muted-foreground)" }}>Last verified</dt>
          <dd style={{ margin: 0 }}>{entry.lastVerifiedAt}</dd>
          {entry.childStatusPages.length > 0 && (
            <>
              <dt style={{ color: "var(--muted-foreground)" }}>Covers</dt>
              <dd style={{ margin: 0 }}>
                One product family. This provider publishes{" "}
                {entry.childStatusPages.length} separate status pages; add the
                others separately if you need them.
              </dd>
            </>
          )}
        </dl>

        {alreadyConfigured && (
          <p
            style={{
              fontSize: 12,
              color: "var(--muted-foreground)",
              margin: 0,
              padding: 10,
              borderRadius: 8,
              background: "var(--muted)",
            }}
          >
            This provider is already being monitored.
          </p>
        )}

        {loading && (
          <div
            role="status"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              color: "var(--muted-foreground)",
            }}
          >
            <Spinner />
            Checking {entry.name}…
          </div>
        )}

        {error && <ErrorNote message={error} />}

        {preview?.kind === "statuspage" && (
          <StatuspagePreviewPanel data={preview.data} />
        )}
        {preview?.kind === "feed" && <FeedPreviewPanel data={preview.data} />}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 8,
            borderTop: "1px solid var(--border)",
          }}
        >
          <Button variant="ghost" icon="back" onClick={onBack}>
            Back to catalog
          </Button>
          <Button
            variant="primary"
            icon="check"
            disabled={saving || alreadyConfigured || !preview}
            onClick={onAdd}
          >
            {saving ? "Adding…" : `Add ${entry.name}`}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function StatuspagePreviewPanel({ data }: { data: StatuspagePreview }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>
        Current state: {data.state}
      </div>
      <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
        {data.summary}
      </div>
      {data.components.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {data.components.slice(0, 12).map((component) => (
            <span
              key={component.name}
              style={{
                fontSize: 11,
                padding: "3px 8px",
                borderRadius: 999,
                background: "var(--muted)",
                color: "var(--muted-foreground)",
              }}
            >
              {component.name}
            </span>
          ))}
        </div>
      )}
      {data.incidents.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
          {data.incidents.map((incident) => (
            <li key={`${incident.title}-${incident.startedAt}`}>
              {incident.title} · {incident.status}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FeedPreviewPanel({ data }: { data: FeedPreview }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>
        Valid feed · {data.entries.length} entries
      </div>
      <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
        channel title "{data.title}"
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
        {data.entries.slice(0, 5).map((row, i) => (
          <li key={i}>
            {row.title} · {row.status}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ErrorNote({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        fontSize: 12,
        color: "var(--destructive)",
        padding: 10,
        borderRadius: 8,
        background: "color-mix(in oklch, var(--destructive) 10%, transparent)",
      }}
    >
      {message}
    </div>
  );
}
